import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { HistoryItem } from "../HistoryDrawer";
import { CompanySelect } from "../Fields/CompanySelect";
import { useCompanyContext } from "../../contexts/CompanyContext";
import { RADIUS } from "../../theme/mixins";
import { Icon } from "../ui/icons";

type HeaderWithButtonProps = {
  icon?: React.ReactNode;
  buttonText?: string; // text inside the button
  title: string; // heading to the right
  eyebrow?: string; // optional small label above the title
  description?: string; // optional description under heading
  onClick?: () => void; // click handler for the button
  endButton?: boolean; // If undefined, will default to true (except dashboard)
  hideEndButton?: boolean; // Explicitly hide the end button
  showCompanyDropdown?: boolean; // Show company dropdown (for super admins on specific pages)
  actionLabel?: string; // primary "+" action shown in the right cluster
  onAction?: () => void;
  actionIcon?: React.ReactNode; // icon shown in the action button; defaults to a "+" icon
  actionButtonSx?: SxProps<Theme>; // style overrides merged over the default primary-button look
  // History drawer props (kept for API compatibility with existing callers)
  historyItems?: HistoryItem[];
  selectedHistoryItemId?: string;
  onHistoryItemClick?: (item: HistoryItem) => void;
  onHistoryItemEdit?: (item: HistoryItem) => void;
  onHistoryItemDelete?: (item: HistoryItem) => void;
};

const HeaderWithButton: React.FC<HeaderWithButtonProps> = ({
  icon,
  buttonText,
  title,
  eyebrow,
  description,
  onClick,
  showCompanyDropdown = false,
  actionLabel,
  onAction,
  actionIcon,
  actionButtonSx,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState(false);
  const { selectedCompanyId, setSelectedCompanyId, userProfile } =
    useCompanyContext();

  // Show company dropdown if prop is true and user is super admin
  const shouldShowCompanyDropdown = showCompanyDropdown && isSuperAdmin;

  // Check if user is superadmin (from cached profile)
  useEffect(() => {
    if (userProfile) {
      const isSuperAdminRole =
        userProfile.roleName?.toLowerCase().includes("super") || false;
      setIsSuperAdmin(isSuperAdminRole);
    }
  }, [userProfile]);

  const companySelect = (
    <CompanySelect
      label={t("components.header.companyLabel")}
      value={selectedCompanyId}
      onChange={(companyId) => setSelectedCompanyId(companyId)}
      required={true}
      showAllOption={true}
    />
  );

  return (
    <Box mb={'28px'}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, minWidth: 0, flex: 1 }}>
          {buttonText && (
            <Box
              component="button"
              onClick={onClick}
              aria-label={buttonText}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                background: theme.palette.common.white,
                border: `1px solid ${theme.palette.border.strong}`,
                color: theme.palette.text.primary,
                borderRadius: `${RADIUS.button}px`,
                padding: "10px 14px",
                marginTop: "4px",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 150ms",
                flex: "none",
                "&:hover": {
                  background: theme.palette.background.neutral,
                  borderColor: theme.palette.border.grey,
                },
              }}
            >
              {icon}
              {!isSmallerScreen && <span>{buttonText}</span>}
            </Box>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            {eyebrow ? (
              <Typography
                variant="overline"
                sx={{ color: theme.palette.brand.icon, marginBottom: "6px" }}
              >
                {eyebrow}
              </Typography>
            ) : null}
            <Typography
              variant="h2"
              sx={{ margin: 0, color: theme.palette.brand.navy, fontWeight: 600 }}
            >
              {title}
            </Typography>
            {description && (
              <Typography
                variant="body1"
                sx={{ color: theme.palette.text.secondary }}
              >
                {description}
              </Typography>
            )}
          </div>
        </div>

        {(actionLabel || (shouldShowCompanyDropdown && !isSmallerScreen)) && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "none" }}>
            {actionLabel && onAction && (
              <Box
                component="button"
                onClick={onAction}
                sx={{
                  ...theme.mixins.primaryButton({
                    height: 48,
                    borderRadius: `${RADIUS.button}px`,
                    padding: "0 16px",
                    fontSize: 15,
                    fontWeight: 600,
                    lineHeight: "26px",
                    whiteSpace: "nowrap",
                  }),
                  "&:hover": {
                    background: theme.palette.brand.luchtHover,
                    transform: "translateY(-1px)",
                  },
                  ...actionButtonSx,
                }}
              >
                {actionIcon ?? <Icon name="plus" size={20} />}
                {actionLabel}
              </Box>
            )}
            {shouldShowCompanyDropdown && !isSmallerScreen && (
              <div style={{ width: 250, flex: "none" }}>{companySelect}</div>
            )}
          </div>
        )}
      </div>

      {shouldShowCompanyDropdown && isSmallerScreen && (
        <div style={{ width: "100%", marginTop: 16 }}>{companySelect}</div>
      )}

      <Snackbar
        open={showWarning}
        autoHideDuration={4000}
        onClose={() => setShowWarning(false)}
      >
        <Alert severity="warning" onClose={() => setShowWarning(false)}>
          {t("components.header.selectCompanyFirstWarning")}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HeaderWithButton;
